import React from "react";
import PropTypes from "prop-types"; // PropTypes for type checking
import { connect } from "react-redux"; // Redux connect for state management
import moment from "moment"; // Moment.js for date and time formatting
import { makeStyles, useTheme } from "@material-ui/core/styles"; // Material-UI styling utilities
import TableSortLabel from "@material-ui/core/TableSortLabel"; // Table sorting label
import MenuItem from "@material-ui/core/MenuItem"; // Menu item for dropdowns
import Grid from "@material-ui/core/Grid"; // Grid layout
import ClickAwayListener from "@material-ui/core/ClickAwayListener"; // ClickAwayListener for closing popups
import Table from "@material-ui/core/Table"; // Table component
import TableHead from "@material-ui/core/TableHead"; // Table header
import isEmpty from "lodash/isEmpty"; // Lodash utility for checking empty objects
import TableBody from "@material-ui/core/TableBody"; // Table body
import TableCell from "@material-ui/core/TableCell"; // Table cell
import TableContainer from "@material-ui/core/TableContainer"; // Table container
import TableFooter from "@material-ui/core/TableFooter"; // Table footer
import useMediaQuery from "@material-ui/core/useMediaQuery"; // Media query hook for responsive design
import TablePagination from "@material-ui/core/TablePagination"; // Table pagination
import TableRow from "@material-ui/core/TableRow"; // Table row
import Paper from "@material-ui/core/Paper"; // Paper component for elevation
import IconButton from "@material-ui/core/IconButton"; // Icon button
import FirstPageIcon from "@material-ui/icons/FirstPage"; // First page icon for pagination
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft"; // Left arrow icon for pagination
import { Typography } from "@material-ui/core"; // Typography for text styling
import Popper from "@material-ui/core/Popper"; // Popper for dropdowns
import Grow from "@material-ui/core/Grow"; // Grow animation
import MenuList from "@material-ui/core/MenuList"; // Menu list for dropdowns
import { withStyles } from "@material-ui/styles"; // Material-UI withStyles for custom styling
import Tooltip from "@material-ui/core/Tooltip"; // Tooltip component
import { format, parseISO, subDays } from "date-fns"; // Date formatting utilities
import InfoIcon from "@material-ui/icons/Info"; // Info icon for additional details
import { useDispatch } from "react-redux"; // Redux dispatch hook
import SyncIcon from "@material-ui/icons/Sync"; // Sync icon for refreshing
import CryptoJS from "crypto-js"; // CryptoJS for encryption/decryption
import AppLoader from "components/AppLoader/Index"; // Custom loader component
import { exportToCSVProvider } from "../../Utils/commonUtils"; // Utility for exporting to CSV
import noDataImage from "../../assets/img/noDataImage.png"; // Image for no data
import SearchTxn from "../../components/DialogBox/SearchTxn"; // Custom search dialog
import TooltipModal from "../../components/Modal/TooltipModal"; // Tooltip modal for additional details
import tableStyle from "../../assets/jss/material-dashboard-react/components/reportTables"; // Custom table styles
import { actionPermission, permissionCheck } from "../../Utils/commonUtils"; // Permission utilities
import { ReactComponent as ExportIcon } from "../../assets/svgIcons/ExportIcon.svg"; // Export icon
import {
  getTransactionListData,
  emptyReduxState,
  getTransactionListDataExport,
  setFormData,
  clearFormData,
  setTransactionListPage,
  setTransactionListRowsPerPage,
} from "../../Redux/Actions/merchantInformation"; // Redux actions for transaction list
import SearchFields from "../../components/SearchComponent/SearchFields"; // Search fields component
import SearchChips from "../../components/SearchComponent/SearchChips"; // Search chips component
import { profileCodePermissionCheck } from "../../Utils/commonUtils"; // Utility for profile code checks

// Custom tooltip styles
const IconsTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#000", // Black background for tooltips
    fontSize: "12px", // Font size for tooltips
  },
  tooltipPlacementTop: {
    margin: "4px 0", // Margin for tooltips placed at the top
  },
}))(Tooltip);

// Custom table styles
const useStyles = makeStyles(tableStyle);

// Function to create table data
function createData(
  senderTitle,
  managerName,
  recipientTitle,
  transactionId,
  amount,
  timestamp,
  status,
  errorCode,
  errorMessage
) {
  return {
    senderTitle, // Sender's title
    managerName, // Manager's name
    recipientTitle, // Recipient's title
    transactionId, // Transaction ID
    amount, // Transaction amount
    timestamp, // Transaction timestamp
    status, // Transaction status
    errorCode, // Error code
    errorMessage, // Error message
  };
}

// Rows for the table
let rows = [];

// Pagination actions for the table
function TablePaginationActions(props) {
  const classes = useStyles();
  const theme = useTheme();

  const {
    count, page, rowsPerPage, onPageChange,
  } = props;

  // Navigate to the first page
  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  // Navigate to the previous page
  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  // Navigate to the next page
  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  // Navigate to the last page
  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.rootPagination}>
      {/* First page button */}
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        variant="body2"
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <Typography>Last</Typography>
        ) : (
          <Typography variant="body2">First</Typography>
        )}
      </IconButton>
      {/* Previous page button */}
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <Typography>Previous</Typography>
        ) : (
          <Typography variant="body2">Previous</Typography>
        )}
      </IconButton>
      {/* Current page indicator */}
      <IconButton
        onClick={handleFirstPageButtonClick}
        aria-label="first page"
        className={classes.bottonPage}
      >
        <Typography variant="body2">{page ? page + 1 : 1}</Typography>
      </IconButton>
      {/* Next page button */}
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <Typography variant="body2">Next</Typography>
        )}
      </IconButton>
      {/* Last page button */}
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon />
        ) : (
          <Typography variant="body2">Last</Typography>
        )}
      </IconButton>
    </div>
  );
}

// Prop types for pagination actions
TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired, // Total number of rows
  onPageChange: PropTypes.func.isRequired, // Function to handle page change
  page: PropTypes.number.isRequired, // Current page number
  rowsPerPage: PropTypes.number.isRequired, // Number of rows per page
};

function EnhancedTableHead(props) {
  const {
    classes, order, orderBy, onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const headCells = [
    {
      id: "senderTitle",
      numeric: false,
      disablePadding: true,
      label: "Sender",
      headClassName: classes.DBA_Name,
    },
    {
      id: "recipientTitle",
      numeric: false,
      disablePadding: false,
      label: "Receiver",
      headClassName: classes.Assign_To,
    },
    {
      id: "transactionId",
      numeric: true,
      disablePadding: false,
      label: "Txn ID",
      headClassName: classes.email,
    },
    {
      id: "amount",
      numeric: true,
      disablePadding: false,
      label: "Amount ($)",
      headClassName: classes.Phone,
    },
    {
      id: "timestamp",
      numeric: true,
      disablePadding: false,
      label: "Date & Time (EDT)",
      headClassName: classes.Phone,
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: "Status",
      headClassName: classes.status,
    },
    {
      id: "errorCode",
      numeric: false,
      disablePadding: false,
      label: "Error Code",
      headClassName: classes.status,
    },
    {
      id: "info",
      numeric: false,
      disablePadding: false,
      label: "",
      headClassName: classes.status,
    },
  ];

  return (
    <TableHead className={classes.tableHeader}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            className={classes.cellTextHeaderOffer}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const TransactionList = (props) => {
  const classes = useStyles();
  const [page, setPage] = React.useState(
    props.transactionListData?.transactionListData?.page
  );
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_ENVIRONMENT;
  const secretKey = process.env.REACT_APP_ENCRIPTION_KEY;
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("timestamp");
  const [selected, setSelected] = React.useState([]);
  const [chipValue, setChipValue] = React.useState([]);
  const [deleteChp, setDeleteChip] = React.useState();
  const theme = useTheme();
  const windowWidth = useMediaQuery(theme.breakpoints.up("md"));
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [openImportFileModal, setImportFileModalOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [searchBar, setSearchBar] = React.useState(false);
  const [id, setId] = React.useState(null);
  const [handleBackPath, setHandleBack] = React.useState(false);
  const profileId = localStorage.getItem("profileId");
  const menu = JSON.parse(localStorage.getItem("menu"));
  const txnList = localStorage.getItem("TransactionReport");
  const searchData = localStorage.getItem("searchData");
  const newSearchData = JSON.parse(searchData);
  const [tooltipContent, setTooltipContent] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalContent, setModalContent] = React.useState([]);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const chips = [];
    if (handleBackPath === true && newSearchData?.searchSenderName) {
      chips.push(`Sender: ${newSearchData?.searchSenderName}`);
    }
    if (handleBackPath === true && newSearchData?.searchTxnID) {
      chips.push(`Txn ID: ${newSearchData?.searchTxnID}`);
    }
    if (
      handleBackPath === true &&
      newSearchData?.searchStartAmount &&
      !newSearchData?.searchEndAmount
    ) {
      if (newSearchData?.amountRange === true) {
        chips.push(
          `Amount From: $${newSearchData?.searchStartAmount} - To End`
        );
      } else {
        chips.push(`Amount: $${newSearchData?.searchStartAmount}`);
      }
    }
    if (
      handleBackPath === true &&
      !newSearchData?.searchStartAmount &&
      newSearchData?.searchEndAmount
    ) {
      if (newSearchData?.amountRange === true) {
        chips.push(
          `Amount From: Start - To $${newSearchData?.searchEndAmount}`
        );
      } else {
        chips.push(`Amount: $${newSearchData?.searchStartAmount}`);
      }
    }
    if (
      handleBackPath === true &&
      newSearchData?.searchStartAmount &&
      newSearchData?.searchEndAmount
    ) {
      chips.push(
        `Amount From: $${newSearchData?.searchStartAmount} - To $${newSearchData?.searchEndAmount}`
      );
    }
    if (
      handleBackPath === true &&
      newSearchData?.searchStartDate &&
      !newSearchData?.searchEndDate
    ) {
      const formattedStartDate = format(
        parseISO(newSearchData?.searchStartDate),
        "MM/dd/yy"
      );
      if (newSearchData?.dateRange === true) {
        chips.push(`Date From: ${formattedStartDate} - To End`);
      } else {
        chips.push(`Date: ${formattedStartDate}`);
      }
    }
    if (
      handleBackPath === true &&
      !newSearchData?.searchStartDate &&
      newSearchData?.searchEndDate
    ) {
      const formattedEndDate = format(
        parseISO(newSearchData?.searchEndDate),
        "MM/dd/yy"
      );
      if (newSearchData?.dateRange === true) {
        chips.push(`Date From: Start - To ${formattedEndDate}`);
      } else {
        chips.push(`Date: ${formattedEndDate}`);
      }
    }
    if (
      handleBackPath === true &&
      newSearchData?.searchStartDate &&
      newSearchData?.searchEndDate
    ) {
      const formattedStartDate = format(
        parseISO(newSearchData?.searchStartDate),
        "MM/dd/yy"
      );
      const formattedEndDate = format(
        parseISO(newSearchData?.searchEndDate),
        "MM/dd/yy"
      );
      chips.push(`Date From: ${formattedStartDate} - To ${formattedEndDate}`);
    }
    if (handleBackPath === true && newSearchData?.searchStatus) {
      chips.push(`Status: ${newSearchData?.searchStatus}`);
    }
    if (handleBackPath === true) {
      setChipValue(chips);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const handleOpenModal = (content) => {
    const dataObj = {
      "Txn ID:": content.transactionId,
      "Amount:": content.amount,
      "Fee $:": parseFloat(content.fee).toFixed(2),
      "Fee Txn ID:": content.feeTransactionId,
      "Fee Txn Status:": content.feeStatus,
      "Refund $:": content.refundAmount,
      "Refund Txn ID:": content.refundTransactionId,
      "Refund Status:": content.refundStatus,
      "Refund Reason Code:": content.refundReasonCode,
    };
    setModalContent(dataObj);
    setModalOpen(true);
  };
  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const idFromUrl = queryParams.get("ID");
    if (idFromUrl) {
      setHandleBack(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleMouseEnter = (amount) => {
    setTooltipContent(amount);
  };

  const handleMouseLeave = () => {
    setTooltipContent([]);
  };

  const handleToggle = () => {
    setImportFileModalOpen((prevOpen) => !prevOpen);
  };

  const handleCloseModel = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setImportFileModalOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setImportFileModalOpen(false);
    }
  }

  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const idFromUrl = queryParams.get("id");
    setId(idFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const chip = [];
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    let today;
    let sevenDaysAgo;
    let startDate;
    let endDate;
    if (props?.transactionListData?.dateRange === false) {
      localStorage.removeItem("isDateRangeChecked");
    }
    if (!handleBackPath) {
      today = new Date();
      sevenDaysAgo = subDays(today, 7);
      startDate = format(sevenDaysAgo, "yyyy-MM-dd");
      endDate = format(today, "yyyy-MM-dd");
      const formattedStartDate = format(parseISO(startDate), "MM/dd/yy");
      const formattedEndDate = format(parseISO(endDate), "MM/dd/yy");
      chip.push(`Date From: ${formattedStartDate} - To ${formattedEndDate}`);
      if (!handleBackPath) {
        localStorage.setItem("isDateRangeChecked", true);
      }
      const updatedFormData = {
        sortColumn: orderBy,
        sortOrder: order,
        recordPerPage: rowsPerPage,
        searchSenderName: "",
        searchTxnID: "",
        searchStartDate: startDate,
        searchEndDate: endDate,
        searchStartAmount: "",
        searchEndAmount: "",
        dateRange: true,
        amountRange: false,
        searchStatus: null,
      };
      setChipValue(chip);
      props.setFormData(updatedFormData);
    }
    if (handleBackPath) {
      props.setFormData(newSearchData);
      setHandleBack("false");
    }
    const payload = {
      Profile_Code: profileCode,
      Profile_id: profileId,
      pageNo: 1,
      recordPerPage: props.transactionListData?.transactionListPagination?.rowsPerPage > 0 ? props.transactionListData?.transactionListPagination?.rowsPerPage : 5,
      sortColumn: orderBy,
      sortOrder: order,
      searchSenderName: newSearchData?.searchSenderName || "",
      searchTxnID: newSearchData?.searchTxnID || "",
      searchStatus: newSearchData?.searchStatus || "",
      searchStartAmount: newSearchData?.searchStartAmount || "",
      searchEndAmount: newSearchData?.searchEndAmount || "",
      searchStartDate: handleBackPath
        ? newSearchData?.searchStartDate || ""
        : startDate,
      searchEndDate: handleBackPath
        ? newSearchData?.searchEndDate || ""
        : endDate,
      dateRange: handleBackPath ? newSearchData?.dateRange || false : true,
      amountRange: newSearchData?.amountRange || false,
    };

    props.getTransactionListData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, handleBackPath]);

  React.useEffect(() => {
    if (txnList) {
      if (props?.transactionListData?.dateRange === false) {
        localStorage.removeItem("isDateRangeChecked");
      }
      localStorage.removeItem("searchData");
      dispatch(setTransactionListPage(1));
      dispatch(setTransactionListRowsPerPage(5));
      setPage(1);
      setRowsPerPage(5);
      const today = new Date();
      const sevenDaysAgo = subDays(today, 7);
      const startDate = format(sevenDaysAgo, "yyyy-MM-dd");
      const endDate = format(today, "yyyy-MM-dd");
      const chips = [];
      const formattedStartDate = format(parseISO(startDate), "MM/dd/yy");
      const formattedEndDate = format(parseISO(endDate), "MM/dd/yy");
      localStorage.setItem("isDateRangeChecked", true);
      chips.push(`Date From: ${formattedStartDate} - To ${formattedEndDate}`);
      setChipValue(chips);
      const updatedFormData = {
        searchSenderName: "",
        searchTxnID: "",
        searchStartDate: startDate,
        searchEndDate: endDate,
        searchStartAmount: "",
        searchEndAmount: "",
        dateRange: true,
        amountRange: false,
        searchStatus: null,
      };
      props.setFormData(updatedFormData);
      props.clearFormData();
      const payload = {
        Profile_id: profileId,
        pageNo: 1,
        recordPerPage: 5,
        sortColumn: orderBy,
        sortOrder: order,
        searchSenderName: "",
        searchTxnID: "",
        searchStartAmount: "",
        searchEndAmount: "",
        searchStartDate: startDate,
        searchEndDate: endDate,
        searchStatus: "",
        dateRange: true,
        amountRange: false,
      };
      props.getTransactionListData(payload);
      localStorage.removeItem("TransactionReport");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txnList]);

  React.useEffect(() => {
    if (props?.transactionListData?.transactionListData?.page !== page) {
      setPage(props?.transactionListData?.transactionListData?.page);
      dispatch(
        setTransactionListPage(
          props?.transactionListData?.transactionListData?.page
        )
      );
    }
    if (
      props?.transactionListData?.transactionListData?.limit !== rowsPerPage
    ) {
      setRowsPerPage(props?.transactionListData?.transactionListData?.limit);
      dispatch(
        setTransactionListRowsPerPage(
          props?.transactionListData?.transactionListData?.limit
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  React.useEffect(
    () => () => {
      const updatedFormData = {
        searchSenderName: "",
        searchTxnID: "",
        searchStartDate: "",
        searchEndDate: "",
        searchStartAmount: "",
        searchEndAmount: "",
        dateRange: false,
        amountRange: false,
        searchStatus: null,
      };
      if (localStorage.getItem("isAmountRangeChecked")) {
        localStorage.removeItem("isAmountRangeChecked");
        updatedFormData.amountRange = false;
      }
      if (localStorage.getItem("isDateRangeChecked")) {
        localStorage.removeItem("isDateRangeChecked");
        updatedFormData.dateRange = false;
      }
      props.setFormData(updatedFormData);
      props.clearFormData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleChangePage = (event, newPage) => {
    dispatch(setTransactionListPage(newPage));
    setPage(newPage);
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.VCS) {
      const requestData = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        Profile_id: profileId,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: orderBy,
        sortOrder: order,
        pageNo: newPage + 1,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(requestData);
    } else {
      const requestData = {
        Profile_id: profileId,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: orderBy,
        sortOrder: order,
        pageNo: newPage + 1,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(requestData);
    }
  };

  window.addEventListener("beforeunload", (event) => {
    if (event.currentTarget.performance.navigation.type === 1) {
      localStorage.removeItem("searchData");
    }
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    dispatch(setTransactionListRowsPerPage(newRowsPerPage));
    setRowsPerPage(parseInt(event.target.value, 10));
    if (newRowsPerPage >= props?.merchantListData?.merchantListData?.total) {
      dispatch(setTransactionListPage(0));
    }
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.SA) {
      const requestData = {
        Profile_id: profileId,
        pageNo: 1,
        recordPerPage: parseInt(event.target.value, 10),
        sortColumn: orderBy,
        sortOrder: order,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        dateRange: props?.transactionListData?.dateRange || false,
        amountRange: props?.transactionListData?.amountRange || false,
      };
      props.getTransactionListData({ ...requestData });
    } else {
      const requestData = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        Profile_id: profileId,
        pageNo: 1,
        recordPerPage: parseInt(event.target.value, 10),
        sortColumn: orderBy,
        sortOrder: order,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",

        dateRange: props?.transactionListData?.dateRange || false,
        amountRange: props?.transactionListData?.amountRange || false,
      };
      props.getTransactionListData(requestData);
    }
  };

  const parseAndFormatDate = (dateString) => {
    const parts = dateString.split("/");
    let year = parts[2];
    if (year.length === 2) {
      const currentYear = new Date().getFullYear().toString();
      const currentCentury = currentYear.slice(0, 2);
      year = `${currentCentury}${year}`;
    }
    const month = parts[1].padStart(2, "0");
    const day = parts[0].padStart(2, "0");
    return `${year}-${day}-${month}`;
  };
  const handleDeleteChip = (chipToDelete) => {
    setDeleteChip(chipToDelete);
    const updatedChips = chipValue.filter((chip) => chip !== chipToDelete);
    setChipValue(updatedChips);
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;

    const updatedFormData = {
      searchSenderName: "",
      searchTxnID: "",
      searchStatus: "",
      searchStartAmount: "",
      searchEndAmount: "",
      searchStartDate: "",
      searchEndDate: "",
      dateRange: false,
      amountRange: false,
    };

    if (updatedChips.length > 0) {
      updatedChips.forEach((chip) => {
        const [key, value] = chip.split(":").map((item) => item.trim());
        switch (key) {
          case "Sender": {
            updatedFormData.searchSenderName = value;
            break;
          }
          case "Txn ID": {
            updatedFormData.searchTxnID = value;
            break;
          }
          case "Status": {
            updatedFormData.searchStatus = value;
            break;
          }
          case "Amount": {
            updatedFormData.searchStartAmount = value.replace(/\$/g, "");
            updatedFormData.amountRange = false;
            break;
          }
          case "Amount From": {
            const samount =
              value === "Start"
                ? value
                : value.split(" - To ")[0].trim().replace(/\$/g, "");
            const eamount =
              value === "End"
                ? value
                : value.split(" - To ")[1].trim().replace(/\$/g, "");
            updatedFormData.amountRange = true;
            updatedFormData.searchStartAmount =
              samount === "Start" ? "" : samount;
            updatedFormData.searchEndAmount = eamount === "End" ? "" : eamount;
            break;
          }
          case "Date": {
            const formattedDate = parseAndFormatDate(value);
            updatedFormData.searchStartDate = formattedDate;
            updatedFormData.dateRange = false;
            break;
          }
          case "Date From": {
            const sdate =
              value === "Start" ? value : value.split(" - To ")[0].trim();
            const edate =
              value === "End" ? value : value.split(" - To ")[1].trim();
            const formattedStartDate =
              sdate === "Start" ? "" : parseAndFormatDate(sdate);
            const formattedEndDate =
              edate === "End" ? "" : parseAndFormatDate(edate);
            updatedFormData.dateRange = true;
            updatedFormData.searchStartDate = formattedStartDate;
            updatedFormData.searchEndDate = formattedEndDate;
            break;
          }
          default:
            break;
        }
      });
    }

    if (
      updatedFormData.searchEndAmount === "" &&
      updatedFormData.searchStartAmount === ""
    ) {
      localStorage.removeItem("isAmountRangeChecked");
    }

    if (
      updatedFormData.searchEndDate === "" &&
      updatedFormData.searchStartDate === ""
    ) {
      localStorage.removeItem("isDateRangeChecked");
    }

    props.clearFormData();
    props.setFormData(updatedFormData);

    const payload = {
      Profile_Code: profileCode,
      Profile_id: profileId,
      pageNo: page > 0 ? page : 1,
      recordPerPage: 5,
      sortColumn: orderBy,
      sortOrder: order,
      ...updatedFormData,
    };

    props.getTransactionListData(payload);

    props.clearFormData();
  };

  const displayAndDeleteChips = (searchData) => {
    setDeleteChip();
    const chips = [];
    if (searchData?.searchSenderName) {
      chips.push(`Sender: ${searchData?.searchSenderName}`);
    }

    if (searchData?.searchTxnID) {
      chips.push(`Txn ID: ${searchData?.searchTxnID}`);
    }
    if (searchData?.searchStartAmount && !searchData?.searchEndAmount) {
      if (searchData?.amountRange === true) {
        chips.push(`Amount From: $${searchData?.searchStartAmount} - To End`);
      } else {
        chips.push(`Amount: $${searchData?.searchStartAmount}`);
      }
    }
    if (!searchData?.searchStartAmount && searchData?.searchEndAmount) {
      if (searchData?.amountRange === true) {
        chips.push(`Amount From: Start - To $${searchData?.searchEndAmount}`);
      } else {
        chips.push(`Amount: $${searchData?.searchStartAmount}`);
      }
    }
    if (searchData?.searchStartAmount && searchData?.searchEndAmount) {
      chips.push(
        `Amount From: $${searchData?.searchStartAmount} - To $${searchData?.searchEndAmount}`
      );
    }
    if (searchData?.searchStartDate && !searchData?.searchEndDate) {
      const formattedStartDate = format(
        parseISO(searchData?.searchStartDate),
        "MM/dd/yy"
      );
      if (searchData?.dateRange === true) {
        chips.push(`Date From: ${formattedStartDate} - To End`);
      } else {
        chips.push(`Date: ${formattedStartDate}`);
      }
    }
    if (!searchData?.searchStartDate && searchData?.searchEndDate) {
      const formattedEndDate = format(
        parseISO(searchData?.searchEndDate),
        "MM/dd/yy"
      );
      if (searchData?.dateRange === true) {
        chips.push(`Date From: Start - To ${formattedEndDate}`);
      } else {
        chips.push(`Date: ${formattedEndDate}`);
      }
    }
    if (searchData?.searchStartDate && searchData?.searchEndDate) {
      const formattedStartDate = format(
        parseISO(searchData?.searchStartDate),
        "MM/dd/yy"
      );
      const formattedEndDate = format(
        parseISO(searchData?.searchEndDate),
        "MM/dd/yy"
      );
      chips.push(`Date From: ${formattedStartDate} - To ${formattedEndDate}`);
    }
    if (searchData?.searchStatus) {
      chips.push(`Status: ${searchData?.searchStatus}`);
    }

    setChipValue(chips);
  };

  rows = [];

  const result = props.transactionListData.transactionListData?.data?.map(
    (items) => ({
      senderTitle: items?.senderTitle,
      managerName: items?.managerName,
      transactionId: items?.transactionId,
      recipientTitle: items?.recipientTitle,
      fee: items?.fee,
      feeTransactionId: items?.feeTransactionId,
      feeStatus: items?.feeStatus,
      feeDate: items?.feeDate,
      refundAmount: items?.refundAmount,
      refundCompleted: items?.refundCompleted,
      refundTransactionId: items?.refundTransactionId,
      refundReasonCode: items?.refundReasonCode,
      refundDate: items?.refundReasonCode,
      error: items?.error,
      receiverFirstName: items?.receiverFirstName,
      receiverLastName: items?.receiverLastName,
      Phone_Number: items?.Phone_Number,
      refundStatus: items.refundStatus,
      amount: items.amount,
      timestamp: items.timestamp,
      refundTimestamp: items.refundTimestamp,
      status: items.status,
      errorCode: items.errorCode,
      errorMessage: items.errorMessage,
    })
  );

  if (result) {
    result.forEach((v) => {
      const senderTitle = v.senderTitle;
      const managerName = v.managerName;
      const recipientTitle = v.recipientTitle;
      const transactionId = v.transactionId;
      const amount = v.amount;
      const fee = v.fee;
      const feeTransactionId = v.feeTransactionId;
      const feeStatus = v.feeStatus;
      const refundAmount = v.refundAmount;
      const refundStatus = v.refundStatus;
      const refundTransactionId = v.refundTransactionId;
      const refundReasonCode = v.refundReasonCode;
      const timestamp = v.timestamp
        ? moment(v.timestamp)
          .tz("America/New_York")
          .format("MM/DD/YYYY hh:mm A")
        : "";
      const status = v.status;
      const errorCode = v.errorCode;
      const errorMessage = v.errorMessage;
      const amountObj = {
        amount,
        fee,
        feeTransactionId,
        feeStatus,
        refundAmount,
        refundStatus,
        refundTransactionId,
        refundReasonCode,
        transactionId,
      };
      rows.push(
        createData(
          senderTitle,
          managerName,
          recipientTitle,
          transactionId,
          amountObj,
          timestamp,
          status,
          errorCode,
          errorMessage
        )
      );
    });
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.SA) {
      const payload = {
        Profile_id: profileId,
        pageNo: page > 0 ? page : 1,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: property,
        sortOrder: newOrder,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(payload);
    } else {
      const payload = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        pageNo: page > 0 ? page : 1,
        Profile_id: profileId,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: property,
        sortOrder: newOrder,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(payload);
    }
  };

  const handleInfoClick = (row) => {
    const localSearch = JSON.parse(localStorage.getItem("searchData"));
    const propsSearch = {
      searchEndAmount: props?.transactionListData?.searchEndAmount,
      searchEndDate: props?.transactionListData?.searchEndDate,
      amountRange: props?.transactionListData?.amountRange,
      dateRange: props?.transactionListData?.dateRange,
      searchSenderName: props?.transactionListData?.searchSenderName,
      searchStartAmount: props?.transactionListData?.searchStartAmount,
      searchStartDate: props?.transactionListData?.searchStartDate,
      searchStatus: props?.transactionListData?.searchStatus,
      searchTxnID: props?.transactionListData?.searchTxnID,
    };

    const isEqual = JSON.stringify(localSearch) === JSON.stringify(propsSearch);
    if (!isEqual) {
      localStorage.setItem("searchData", JSON.stringify(propsSearch));
    }

    if (props?.transactionListData?.dateRange === false) {
      localStorage.removeItem("isDateRangeChecked");
    }
    const updatedData = {
      searchTxnID: "",
    };
    updatedData.searchTxnID = row.transactionId;
    props.history.push(
      `/${props.userDetails.layout}/fee-refund-history?searchTxnID=${row.transactionId}`
    );
  };

  const exportedBy =
    props.mainData?.users?.userDetailsById &&
    props.mainData?.users?.userDetailsById?.Users_Data[0]?.Email;
  const exportedByFname =
    props.mainData.users.userDetailsById &&
    props.mainData.users.userDetailsById?.Users_Data[0].FName;
  const exportedByLname =
    props.mainData.users.userDetailsById &&
    props.mainData.users.userDetailsById?.Users_Data[0].LName;

  const exportListData = () => {
    setLoading(true);
    const filterData = {
      Profile_id: profileId,
      pageNo: page,
      recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
      sortColumn: orderBy,
      sortOrder: order,
      searchSenderName: props?.transactionListData?.searchSenderName || "",
      searchTxnID: props?.transactionListData?.searchTxnID || "",
      searchStartAmount: props?.transactionListData?.searchStartAmount || "",
      searchEndAmount: props?.transactionListData?.searchEndAmount || "",
      searchStartDate: props?.transactionListData?.searchStartDate || "",
      searchEndDate: props?.transactionListData?.searchEndDate || "",
      searchStatus: props?.transactionListData?.searchStatus || "",
      dateRange: props?.transactionListData?.dateRange || "",
      amountRange: props?.transactionListData?.amountRange || "",
      export: true
    };
    filterData.recordPerPage = 99999999;
    filterData.pageNo = 1;
    props.getTransactionListDataExport(filterData, (data) => {
      setLoading(false);
      if (data) {
        const bytes = CryptoJS.AES.decrypt(data.data, secretKey);
        const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
        const parsedData =
          encryptionKey === "NO" ? data.data : JSON.parse(decryptedData);
        const mappedLogs = parsedData.map((row) => ({
          Sender: row.senderTitle,
          Receiver: row.recipientTitle,
          "Txn ID": row.transactionId,
          "Amount ($)": row.amount ? parseFloat(row.amount) : 0,
          "Date & Time (EDT)": moment(row.timestamp)
            .tz("America/New_York")
            .format("MM/DD/YYYY hh:mm A"),
          Status: row.status,
          "Fee ($)": row.fee ? parseFloat(row.fee) : 0,
          "Fee Txn ID": row.feeTransactionId,
          "Fee Status": row.feeStatus,
          "Error Code": row.errorCode,
          "Error Description": row.errorMessage,
          "Fee Date": moment(row.feeDate)
            .tz("America/New_York")
            .format("MM/DD/YYYY hh:mm A"),
          "Refund ($)": row.refundAmount ? parseFloat(row.refundAmount) : 0,
          "Refund Txn ID": row.refundTransactionId,
          "Refund Status": row.refundStatus,
          "Refund Date": moment(row.refundTimestamp)
            .tz("America/New_York")
            .format("MM/DD/YYYY hh:mm A"),
          "Reason Code": row.error,
        }));
        exportToCSVProvider(
          mappedLogs,
          "Transaction Report",
          chipValue,
          exportedBy,
          exportedByFname,
          exportedByLname
        );
      }
    });
  };

  // const exportToPDFList = () => {
  //   const filterData = {
  //     ...props.tableData.searchDetails,
  //     Profile_id: profileId,
  //     pageNo: page,
  //     recordPerPage: rowsPerPage,
  //     sortColumn: orderBy,
  //     sortOrder: order,
  //     searchSenderName: newSearchData?.searchSenderName
  //       ? newSearchData?.searchSenderName
  //       : "",
  //     searchTxnID: newSearchData?.searchTxnID ? newSearchData?.searchTxnID : "",
  //     searchStartAmount: newSearchData?.searchStartAmount
  //       ? newSearchData?.searchStartAmount
  //       : "",
  //     searchEndAmount: newSearchData?.searchEndAmount
  //       ? newSearchData?.searchEndAmount
  //       : "",
  //     searchStartDate: newSearchData?.searchStartDate
  //       ? newSearchData?.searchStartDate
  //       : "",
  //     searchEndDate: newSearchData?.searchEndDate
  //       ? newSearchData?.searchEndDate
  //       : "",
  //     searchStatus: newSearchData?.searchStatus
  //       ? newSearchData?.searchStatus
  //       : "",
  //     dateRange: newSearchData?.dateRange ? newSearchData?.dateRange : "",
  //     amountRange: newSearchData?.amountRange ? newSearchData?.amountRange : "",
  //   };
  //   filterData.recordPerPage = props.transactionListData.transactionListData.total;
  //   filterData.pageNo = 1;
  //   props.getTransactionListDataExport(filterData, (data) => {
  //     const totalCount = data.data.length;
  //     if (data) {
  //       const mappedLogs = data.data.map((row) => [
  //         row.senderTitle,
  //         row.recipientTitle,
  //         row.transactionId,
  //         row.parseFloat(row.amount).toFixed(2),
  //         moment(row.timestamp).tz('America/New_York').format("MM/DD/YYYY hh:mm A"),
  //         row.status,
  //         row.fee,
  //         row.feeTransactionId,
  //         row.feeStatus,
  //         row.feeDate,
  //         row.refundAmount,
  //         row.refundTransactionId,
  //         row.refundStatus,
  //         row.refundDate,
  //         row.reasonCode,
  //         row.referenceTransactionId,
  //       ]);

  //       const headersName = [
  //         "Sender",
  //         "Receiver",
  //         "Txn ID",
  //         "Amount ($)",
  //         "Date & Time (EDT)",
  //         "Status",
  //         "Fee ($)",
  //         "Fee Txn ID",
  //         "Fee Status",
  //         "Fee Date",
  //         "Refund ($)",
  //         "Refund Txn ID",
  //         "Refund Status",
  //         "Refund Date",
  //         "Reason Code",
  //         "Reference Txn ID",
  //       ];
  //       exportToPDF(
  //         headersName,
  //         mappedLogs,
  //         "Transaction Report",
  //         chipValue,
  //         exportedBy,
  //         exportedByFname,
  //         exportedByLname,
  //         totalCount
  //       );
  //     }
  //   });
  // };

  const handleSearchOpen = () => {
    setSearchBar(true);
  };

  const handleSearchClose = () => {
    setSearchBar(false);
  };

  const handleRefresh = () => {
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.VCS) {
      const requestData = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: orderBy,
        sortOrder: order,
        pageNo: page > 0 ? page : 1,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(requestData);
    } else {
      const requestData = {
        Profile_id: profileId,
        recordPerPage: rowsPerPage > 0 ? rowsPerPage : 5,
        sortColumn: orderBy,
        sortOrder: order,
        pageNo: page > 0 ? page : 1,
        searchSenderName: props?.transactionListData?.searchSenderName || "",
        searchTxnID: props?.transactionListData?.searchTxnID || "",
        searchStartAmount: props?.transactionListData?.searchStartAmount || "",
        searchEndAmount: props?.transactionListData?.searchEndAmount || "",
        searchStartDate: props?.transactionListData?.searchStartDate || "",
        searchEndDate: props?.transactionListData?.searchEndDate || "",
        searchStatus: props?.transactionListData?.searchStatus || "",
        dateRange: props?.transactionListData?.dateRange || "",
        amountRange: props?.transactionListData?.amountRange || "",
      };
      props.getTransactionListData(requestData);
    }
  };

  return (
    <>
      <AppLoader loader={loading} />
      <TableContainer className={classes.TableContainer} component={Paper}>
        {windowWidth && (
          <Grid container className={classes.searchContainer}>
            <Grid item className={classes.margin}>
              <div className={classes.searchWrapper}>
                <SearchFields
                  searchBar={searchBar}
                  handleSearchClose={handleSearchClose}
                  handleSearchOpen={handleSearchOpen}
                  Fields={() => (
                    <SearchTxn
                      close={() => handleSearchClose()}
                      seacrhChipData={(data) => displayAndDeleteChips(data)}
                      clearChips={() => setChipValue([])}
                      deleteChp={deleteChp}
                    />
                  )}
                />
                <IconsTooltip title="Refresh" placement="top">
                  <div className={classes.appSvgIcons}>
                    <SyncIcon
                      style={{ marginRight: "10px" }}
                      fontSize="large"
                      color="primary"
                      onClick={handleRefresh}
                    />
                  </div>
                </IconsTooltip>
                <IconsTooltip title="Export" placement="top">
                  <div className={classes.appSvgIcons}>
                    <ExportIcon
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      onClick={handleToggle}
                      ref={anchorRef}
                    />
                  </div>
                </IconsTooltip>
                {actionPermission(
                  permissionCheck.Category.setup,
                  permissionCheck.Page.merchant,
                  permissionCheck.option.export
                ) &&
                  (actionPermission(
                    permissionCheck.Category.setup,
                    permissionCheck.Page.merchant,
                    permissionCheck.option.exportToExcel
                  ) ||
                    actionPermission(
                      permissionCheck.Category.setup,
                      permissionCheck.Page.merchant,
                      permissionCheck.option.exportToPdf
                    )) && (
                    <IconsTooltip title="Export" placement="top">
                      <div className={classes.appSvgIcons}>
                        <ExportIcon
                          aria-controls="simple-menu"
                          aria-haspopup="true"
                          onClick={handleToggle}
                          ref={anchorRef}
                        />
                      </div>
                    </IconsTooltip>
                  )}
                <Popper
                  open={openImportFileModal}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{
                    zIndex: "10",
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseModel}>
                          <MenuList
                            id="menu-list-grow"
                            onKeyDown={handleListKeyDown}
                          >
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToExcel
                            ) && (
                                <MenuItem
                                  onClick={exportListData}
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  Export to Excel
                                </MenuItem>
                              )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </Grid>
          </Grid>
        )}
        {!windowWidth && (
          <Grid container className={classes.searchContainer}>
            <Grid item className={classes.margin} style={{ display: "flex" }}>
              <div className={classes.searchWrapper}>
                <SearchFields
                  searchBar={searchBar}
                  handleSearchClose={handleSearchClose}
                  handleSearchOpen={handleSearchOpen}
                  Fields={() => (
                    <SearchTxn
                      close={() => handleSearchClose()}
                      seacrhChipData={(data) => displayAndDeleteChips(data)}
                      clearChips={() => setChipValue([])}
                    />
                  )}
                />
                <IconsTooltip title="Export" placement="top">
                  <div className={classes.appSvgIcons}>
                    <ExportIcon
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      onClick={handleToggle}
                      ref={anchorRef}
                    />
                  </div>
                </IconsTooltip>
                <Popper
                  open={openImportFileModal}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{
                    zIndex: "10",
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseModel}>
                          <MenuList
                            id="menu-list-grow"
                            onKeyDown={handleListKeyDown}
                          >
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToExcel
                            ) && (
                                <MenuItem
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  Export to Excel
                                </MenuItem>
                              )}
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToPdf
                            ) && (
                                <MenuItem
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  Export to PDF
                                </MenuItem>
                              )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </Grid>
          </Grid>
        )}
        <SearchChips
          searchValues={chipValue}
          handleDeleteChip={(chip, index) => handleDeleteChip(chip, index)}
        />
        <Table className={classes.table} aria-label="simple table">
          <EnhancedTableHead
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
          />
          <TableBody>
            {!isEmpty(rows) ? (
              rows.map((row, index) => (
                <TableRow className={classes.cellHeadSign} key={index}>
                  <TableCell align="center" className={classes.cellText}>
                    <div>
                      <div>{row.senderTitle}</div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#794411",
                        }}
                      >
                        {row.managerName}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    {row.recipientTitle}
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    {row.transactionId}
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    <IconsTooltip
                      title={(
                        <div
                          style={{
                            width: "200px",
                            fontSize: "13px",
                          }}
                        >
                          {tooltipContent.transactionId && (
                            <div>
                              <strong>Txn ID:</strong>
                              {" "}
                              {tooltipContent.transactionId}
                            </div>
                          )}
                          {tooltipContent.amount && (
                            <div>
                              <strong>Amount:</strong>
                              {" "}
                              {tooltipContent.amount}
                            </div>
                          )}
                          {tooltipContent.fee && (
                            <div>
                              <strong>Fee $:</strong>
                              {" "}
                              {parseFloat(tooltipContent.fee).toFixed(2)}
                            </div>
                          )}
                          {tooltipContent.feeTransactionId && (
                            <div>
                              <strong>Fee Txn ID:</strong>
                              {" "}
                              {tooltipContent.feeTransactionId}
                            </div>
                          )}
                          {tooltipContent.feeStatus && (
                            <div>
                              <strong>Fee Txn Status:</strong>
                              {" "}
                              {tooltipContent.feeStatus}
                            </div>
                          )}
                          {tooltipContent.refundAmount && (
                            <div>
                              <strong>Refund $:</strong>
                              {" "}
                              {tooltipContent.refundAmount}
                            </div>
                          )}
                          {tooltipContent.refundTransactionId && (
                            <div>
                              <strong>Refund Txn ID:</strong>
                              {" "}
                              {tooltipContent.refundTransactionId}
                            </div>
                          )}
                          {tooltipContent.refundStatus && (
                            <div>
                              <strong>Refund Status:</strong>
                              {" "}
                              {tooltipContent.refundStatus}
                            </div>
                          )}
                          {tooltipContent.refundReasonCode && (
                            <div>
                              <strong>Refund Reason Code:</strong>
                              {" "}
                              {tooltipContent.refundReasonCode}
                            </div>
                          )}
                        </div>
                      )}
                      placement="top"
                    >
                      <button
                        type="button"
                        style={{
                          background: "none",
                          border: "none",
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer",
                          padding: 0,
                          font: "inherit",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenModal(tooltipContent);
                        }}
                        onMouseEnter={() => handleMouseEnter(row.amount)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {parseFloat(row.amount.amount).toFixed(2)}
                      </button>
                    </IconsTooltip>
                    <Popper
                      open={false}
                      anchorEl={anchorRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                      style={{
                        zIndex: "10",
                      }}
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin:
                              placement === "bottom-start"
                                ? "left top"
                                : "left bottom",
                          }}
                        >
                          <IconsTooltip title="tooltip" placement="top">
                            {" "}
                            <div
                              style={{
                                width: "200px",
                                fontSize: "13px",
                              }}
                            >
                              {tooltipContent.transactionId && (
                                <div>
                                  <strong>Txn ID:</strong>
                                  {" "}
                                  {tooltipContent.transactionId}
                                </div>
                              )}
                              {tooltipContent.amount && (
                                <div>
                                  <strong>Amount:</strong>
                                  {" "}
                                  {tooltipContent.amount}
                                </div>
                              )}
                              {tooltipContent.fee && (
                                <div>
                                  <strong>Fee $:</strong>
                                  {" "}
                                  {tooltipContent.fee}
                                </div>
                              )}
                              {tooltipContent.feeTransactionId && (
                                <div>
                                  <strong>Fee Txn ID:</strong>
                                  {" "}
                                  {tooltipContent.feeTransactionId}
                                </div>
                              )}
                              {tooltipContent.feeStatus && (
                                <div>
                                  <strong>Fee Txn Status:</strong>
                                  {" "}
                                  {tooltipContent.feeStatus}
                                </div>
                              )}
                              {tooltipContent.refundAmount && (
                                <div>
                                  <strong>Refund $:</strong>
                                  {" "}
                                  {tooltipContent.refundAmount}
                                </div>
                              )}
                              {tooltipContent.refundTransactionId && (
                                <div>
                                  <strong>Refund Txn ID:</strong>
                                  {" "}
                                  {tooltipContent.refundTransactionId}
                                </div>
                              )}
                              {tooltipContent.refundStatus && (
                                <div>
                                  <strong>Refund Status:</strong>
                                  {" "}
                                  {tooltipContent.refundStatus}
                                </div>
                              )}
                              {tooltipContent.refundReasonCode && (
                                <div>
                                  <strong>Refund Reason Code:</strong>
                                  {" "}
                                  {tooltipContent.refundReasonCode}
                                </div>
                              )}
                            </div>
                          </IconsTooltip>
                        </Grow>
                      )}
                    </Popper>
                  </TableCell>
                  <TooltipModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    content={modalContent}
                  />
                  <TableCell className={classes.cellText} align="center">
                    {row.timestamp}
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    {row.status}
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    <IconsTooltip
                      title={(
                        <div
                          style={{
                            width: "200px",
                            fontSize: "13px",
                          }}
                        >
                          <div>{row.errorMessage}</div>
                        </div>
                      )}
                      placement="top"
                    >
                      <div
                        href="#"
                        style={{
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      // onMouseEnter={() => handleMouseEnter(row.errorMessage)}
                      // onMouseLeave={handleMouseLeave}
                      >
                        {row.errorCode}
                      </div>
                    </IconsTooltip>
                  </TableCell>
                  <TableCell className={classes.cellText} align="center">
                    <Tooltip
                      title={<div>View Details</div>}
                      placement="right"
                      arrow
                    >
                      <InfoIcon
                        fontSize="small"
                        color="action"
                        onClick={() => handleInfoClick(row)}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="no-data" colSpan={12}>
                  <img
                    src={noDataImage}
                    alt=""
                    className={classes.noDataImage}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {rows.length ? (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[
                    5,
                    10,
                    25,
                    {
                      label: "All",
                      value:
                        props?.transactionListData?.transactionListData?.total,
                    },
                  ]}
                  colSpan={12}
                  count={props?.transactionListData?.transactionListData?.total}
                  rowsPerPage={rowsPerPage || 5}
                  page={page > 0 ? page - 1 : 0}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    MenuProps: { classes: { paper: classes.selectDropdown } },
                  }}
                  classes={{ menuItem: classes.menuItem }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          ) : (
            ""
          )}
        </Table>
      </TableContainer>
    </>
  );
};

TransactionList.propTypes = {
  tableData: PropTypes.object,
  history: PropTypes.any,
  userDetails: PropTypes.object,
  mainData: PropTypes.object,
  getTransactionListData: PropTypes.func,
  getTransactionListDataExport: PropTypes.func,
  emptyReduxState: PropTypes.func,
};

const mapStateToProps = (app) => ({
  tableData: app.provideRegistration,
  userDetails: app.app,
  mainData: app,
  roles: app.roles,
  transactionListData: app.merchantInformation,
});
export default connect(mapStateToProps, {
  setFormData,
  clearFormData,
  getTransactionListData,
  getTransactionListDataExport,
  emptyReduxState,
})(TransactionList);
